import { useEffect, useState, type ReactNode } from 'react';
import { errorText } from './api';

export function Btn({
  children,
  kind = 'primary',
  busy,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { kind?: 'primary' | 'secondary' | 'danger' | 'ghost'; busy?: boolean }) {
  return (
    <button {...rest} disabled={rest.disabled || busy} className={`btn btn-${kind} ${rest.className ?? ''}`}>
      {busy ? '…' : children}
    </button>
  );
}

export function Loading() {
  return <div className="muted pad">Загрузка…</div>;
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div className="error-box" role="alert">
      {errorText(error)}
      {onRetry ? (
        <button className="link" onClick={onRetry}>
          Повторить
        </button>
      ) : null}
    </div>
  );
}

export function Empty({ text = 'Ничего не найдено' }: { text?: string }) {
  return <div className="muted pad">{text}</div>;
}

export function Pager({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="pager">
      <Btn kind="secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        ←
      </Btn>
      <span>
        {page} / {pages} · всего {total}
      </span>
      <Btn kind="secondary" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        →
      </Btn>
    </div>
  );
}

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  // Esc закрывает окно (если фокус не в поле ввода с собственной обработкой).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{title}</h3>
          <button className="link" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Запуск асинхронного действия с отображением ошибки и состояния выполнения. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const run = async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(e);
      return undefined;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, run, clear: () => setError(null) };
}

export const fmtDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }) : '—');

export const STATUS_RU: Record<string, string> = {
  draft: 'Черновик',
  pending: 'На модерации',
  active: 'Активно',
  rejected: 'Отклонено',
  archived: 'Архив',
  deleted: 'Удалено',
  open: 'Открыта',
  resolved: 'Решена',
  dismissed: 'Отклонена',
  blocked: 'Заблокирован',
  completed: 'Выполнен',
};

export const REJECT_REASONS: Record<string, string> = {
  prohibited: 'Запрещённый товар/услуга',
  bad_photos: 'Плохие фото',
  wrong_category: 'Неверная категория',
  incomplete: 'Неполная информация',
  duplicate: 'Дубликат',
  fraud_suspected: 'Подозрение на мошенничество',
  other: 'Другое',
};

export const REPORT_REASONS: Record<string, string> = {
  fraud: 'Мошенничество',
  prohibited: 'Запрещённый товар',
  wrong_info: 'Неверная информация',
  duplicate: 'Дубликат',
  spam: 'Спам',
  rules_violation: 'Нарушение правил',
  other: 'Другое',
};

export const ROLE_RU: Record<string, string> = {
  owner: 'Владелец',
  admin: 'Администратор',
  moderator: 'Модератор',
  ads_manager: 'Менеджер рекламы',
};
